// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import Badge from "@insite/mobius/Badge";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getIsNonPurchasable } from "@keller/App/Store/Selectors/GeneralSelectors";
import ProductBrand, { ProductBrandStyles } from "@keller/Overrides/Components/ProductBrand";
import { constants } from "@keller/XDL/constants";
import Arrows from "@keller/XDL/Icons/Arrows";
import Close from "@keller/XDL/Icons/Close";
import { color, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showImage: boolean;
    showCompare: boolean;
    extendedStyles?: ProductListProductImageStyles;
    showTextBadge?: boolean;
    showImageBadge?: boolean;
    // XNG-Changes
    showBrands?: boolean;
    view?: ProductListViewType;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => ({
    settingsCollection: getSettingsCollection(state),
    productCompareChecked: getProductCompareChecked(state, props.productContext),
    isProductCompareFull: getIsProductCompareFull(state),
    isNonPurchasable: getIsNonPurchasable(state),
});

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasProductContext;

export interface ProductListProductImageStyles {
    wrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    productBrand?: ProductBrandStyles;
    compareCheckbox?: CheckboxPresentationProps;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

export const productImageStyles: ProductListProductImageStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            max-width: 100px;
            align-items: center;
        `,
    },
    compareCheckbox: {
        css: css`
            align-self: center;
            margin-left: auto;
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 100px;
                height: 100px;
                margin-bottom: 8px;
                background: ${color("common.white")};
                justify-content: center;
            `,
        },
        img: {
            css: css`
                width: auto;
                height: auto;
                max-width: 100%;
                max-height: 100%;
                top: 0;
                left: 0;
                z-index: 0;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 75%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 10px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    productBrand: {
        nameLink: {
            css: css`
                justify-content: center;
                text-align: center;
                margin-left: auto;
                margin-right: auto;
            `,
        },
        logoImage: {
            width: "80px",
            height: "22px",
            css: css`
                img {
                    max-width: 100%;
                    max-height: 100%;
                }
            `,
        },
    },
};

const { productGridView: productGridViewScreen } = constants;
export const productGridImageStyles: ProductListProductImageStyles = {
    ...productImageStyles,
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 100%;
                justify-content: center;
                background: ${color("common.white")};
                margin-bottom: 12px;

                height: 308px;
                ${xScreen("lte", "sm")} {
                    height: 230px;
                }
                ${xScreen("lte", "xs")} {
                    height: 292px;
                }
            `,
        },
        img: {
            css: css`
                width: auto;
                max-width: 100%;
                max-height: 100%;
                margin: initial;
                object-fit: contain;
                aspect-ratio: 1/1;
            `,
        },
    },
};

const ProductListProductImage = ({
    productContext,
    showImage,
    showTextBadge,
    showImageBadge,
    showCompare,
    isProductCompareFull,
    settingsCollection,
    productCompareChecked,
    extendedStyles,
    addProductToCompare,
    removeProductFromCompare,
    showBrands,
    isNonPurchasable,
    view,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const stylesByView = view === "List" ? productImageStyles : productGridImageStyles;
    const [styles] = useState(() => mergeWithCss(stylesByView, extendedStyles));

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge,
        showImageBadge,
        styles,
    });

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };

    // XNG-Changes: Update structure
    return (
        <>
            {(showImage || showBrands) && (
                <StyledWrapper
                    {...styles.wrapper}
                    data-test-selector="productListProductImage"
                    data-test-key={productContext.product.id}
                >
                    {showImage && !showOverlayBadge && (
                        <ProductImage extendedStyles={styles.productImage} product={productContext} />
                    )}
                    {showImage && showOverlayBadge && overlayBadge && (
                        <StyledWrapper
                            {...styles.badgeAreaWrapper}
                            ref={badgeAreaWrapperRef}
                            data-test-selector="productListBadge"
                        >
                            <ProductImage extendedStyles={styles.productImage} product={productContext} />
                            <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                                <Badge
                                    type={overlayBadge.badgeType}
                                    value={
                                        overlayBadge.badgeType === "Image"
                                            ? overlayBadge.largeImageBadgePath
                                            : overlayBadge.displayText
                                    }
                                    badgeTextColor={overlayBadge.textColorHexCode}
                                    badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                    badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                    imageAltText={
                                        overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                    }
                                    textBadgeCss={styles.badgeText}
                                    imageBadgeProps={styles.badgeImageProps}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {showBrands && productContext.product.brand && (
                        <ProductBrand
                            extendedStyles={styles.productBrand}
                            brand={productContext.product.brand}
                            showLogo={view === "List"}
                        />
                    )}
                </StyledWrapper>
            )}
            {showCompare && settingsCollection.productSettings.enableProductComparisons && (
                <Checkbox
                    {...styles.compareCheckbox}
                    checked={productCompareChecked}
                    onChange={handleChanged}
                    data-test-selector="productList_CompareCheckbox"
                >
                    {translate("Compare")}
                </Checkbox>
            )}
        </>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListProductImage));
